import { Link, useNavigate } from "react-router-dom";
import { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowRight,
  Sparkles,
  Palette,
  ShieldCheck,
  Image as ImageIcon,
  Camera,
  Upload,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useUploadDraft } from "@/contexts/UploadDraftContext";
import { isTouchDevice } from "@/lib/device";
import usePageMeta from "@/hooks/usePageMeta";

// HOMEPAGE-01 · Concept A · Queenslander (2026-07-04)
// See /app/memory/PRD.md for the design decisions behind this rewrite.

const HERO_BEFORE = "/branding/homepage/before-hero-queenslander.jpg";
const HERO_AFTER = "/branding/homepage/after-hero-queenslander.jpg";

// HERO-SWATCH-DEMO (2026-02, upgraded to 4-surface per COLOURMATCH PLATFORM
// DIRECTION LOCKED): Walls · Fascia+Gutters (locked together as "Trim") ·
// Front Door. Richer, darker wall palette so the transformation reads within
// the first five seconds a visitor lands. 2 walls × 2 trims × 2 doors = 8
// pre-rendered AFTER images (regenerated by scripts/render_hero_combos_v2.py).
const HERO_SWATCH_DIR = "/branding/homepage/hero-swatches";
const WALL_SWATCHES = [
  { id: "coastal_blue", name: "Coastal Blue", hex: "#526A75" },
  { id: "sage_grey",    name: "Sage Grey",    hex: "#6D7565" },
];
const TRIM_SWATCHES = [
  { id: "white_trim",    name: "Classic White", hex: "#F5F1E6" },
  { id: "charcoal_trim", name: "Charcoal Trim", hex: "#2E3437" },
];
const DOOR_SWATCHES = [
  { id: "charcoal_door", name: "Charcoal",  hex: "#2E2E2E" },
  { id: "navy_door",     name: "Deep Navy", hex: "#1F3A5F" },
];
const heroSrc = (wallId, trimId, doorId) =>
  `${HERO_SWATCH_DIR}/hero-${wallId}-${trimId}-${doorId}.jpg`;

// Gallery tiles — each `featured: true` tile spans 2 columns on desktop.
// Kitchen is the strongest asset in the batch (founder pick) so it gets
// the featured slot.
const GALLERY = [
  { id: "int2", featured: true, category: "Interior", subject: "Kitchen",
    colour: "Apothecary Green", hex: "#3D5640",
    before: "/branding/homepage/before-int2-kitchen.jpg",
    after: "/branding/homepage/after-int2-kitchen.jpg" },
  { id: "ext1", category: "Exterior", subject: "Queenslander",
    colour: "Aged Cedar", hex: "#7B6048",
    before: "/branding/homepage/before-ext1-queenslander.jpg",
    after: "/branding/homepage/after-ext1-queenslander.jpg" },
  { id: "ext2", category: "Exterior", subject: "Brick veneer",
    colour: "Anchor Blue", hex: "#27425A",
    before: "/branding/homepage/before-ext2-brick-veneer.jpg",
    after: "/branding/homepage/after-ext2-brick-veneer.jpg" },
  { id: "int1", category: "Interior", subject: "Living room",
    colour: "Antique Rose", hex: "#B57777",
    before: "/branding/homepage/before-int1-living.jpg",
    after: "/branding/homepage/after-int1-living.jpg" },
  { id: "int3", category: "Interior", subject: "Bedroom",
    colour: "Aged Cedar", hex: "#7B6048",
    before: "/branding/homepage/before-int3-bedroom.jpg",
    after: "/branding/homepage/after-int3-bedroom.jpg" },
  { id: "ext3", category: "Exterior", subject: "Contemporary home",
    colour: "Anthracite", hex: "#2B2D2E",
    before: "/branding/homepage/before-ext3-contemporary.jpg",
    after: "/branding/homepage/after-ext3-contemporary.jpg" },
];

const TRUST = [
  { icon: Sparkles,   label: "Free preview",             sub: "Try one render before you pay" },
  { icon: ImageIcon,  label: "Photo-real preview",       sub: "See the exact result before painting" },
  { icon: Palette,    label: "1,000+ designer colours",  sub: "Curated interior + exterior library" },
  { icon: ShieldCheck,label: "Secure Stripe payments",   sub: "No subscription · one-off $29.99" },
];

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] },
};

/**
 * Lean before/after slider — no external dependency. Drag or click to reveal.
 * Works with mouse + touch + keyboard (arrow keys, Home/End when focused).
 */
function BeforeAfterSlider({ before, after, alt, testid = "hero-slider" }) {
  const [pct, setPct] = useState(50);
  const containerRef = useRef(null);
  const dragging = useRef(false);

  const setFromClientX = useCallback((clientX) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = Math.min(Math.max(clientX - rect.left, 0), rect.width);
    setPct((x / rect.width) * 100);
  }, []);

  useEffect(() => {
    const move = (e) => {
      if (!dragging.current) return;
      const x = e.touches ? e.touches[0].clientX : e.clientX;
      setFromClientX(x);
    };
    const up = () => { dragging.current = false; };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    window.addEventListener("touchmove", move, { passive: true });
    window.addEventListener("touchend", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("touchend", up);
    };
  }, [setFromClientX]);

  const startDrag = (e) => {
    dragging.current = true;
    const x = e.touches ? e.touches[0].clientX : e.clientX;
    setFromClientX(x);
  };

  const onKey = (e) => {
    if (e.key === "ArrowLeft")  setPct((p) => Math.max(0, p - 4));
    if (e.key === "ArrowRight") setPct((p) => Math.min(100, p + 4));
    if (e.key === "Home")       setPct(0);
    if (e.key === "End")        setPct(100);
  };

  return (
    <div
      ref={containerRef}
      data-testid={testid}
      className="relative w-full aspect-[16/9] rounded-3xl overflow-hidden shadow-soft-lg bg-brand-ink select-none cursor-ew-resize"
      onMouseDown={startDrag}
      onTouchStart={startDrag}
    >
      <img src={before} alt={`${alt} — before`} className="absolute inset-0 w-full h-full object-cover" draggable={false} />
      <div className="absolute inset-0 overflow-hidden" style={{ clipPath: `inset(0 0 0 ${pct}%)` }}>
        <img src={after} alt={`${alt} — after`} className="absolute inset-0 w-full h-full object-cover" draggable={false} />
      </div>

      {/* Labels */}
      <div className="absolute top-3 left-3 bg-white/85 backdrop-blur-sm text-brand-ink text-[10px] font-bold tracking-widest uppercase px-2.5 py-1 rounded-full">
        Before
      </div>
      <div className="absolute top-3 right-3 bg-brand-navy text-white text-[10px] font-bold tracking-widest uppercase px-2.5 py-1 rounded-full">
        After
      </div>

      {/* Divider — 2 px white bar wrapped in a dark outer shadow so it stays
          legible against both light sky and dark verandah alike. */}
      <div
        className="absolute top-0 bottom-0 w-[2px] bg-white shadow-[0_0_0_1px_rgba(0,0,0,0.35),0_0_18px_rgba(0,0,0,0.45)]"
        style={{ left: `${pct}%`, transform: "translateX(-1px)" }}
      >
        <button
          type="button"
          role="slider"
          aria-label="Drag to compare before and after"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(pct)}
          tabIndex={0}
          onKeyDown={onKey}
          data-testid={`${testid}-handle`}
          className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white ring-2 ring-black/20 shadow-[0_6px_22px_rgba(0,0,0,0.45)] flex items-center justify-center text-brand-ink hover:scale-105 transition-transform focus:outline-none focus:ring-4 focus:ring-brand-teal/60"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="9 6 3 12 9 18" />
            <polyline points="15 6 21 12 15 18" />
          </svg>
        </button>
      </div>
    </div>
  );
}

/** Static side-by-side gallery tile with a small always-visible reveal on hover. */
function GalleryTile({ tile }) {
  const [showing, setShowing] = useState("after");
  const testid = `gallery-tile-${tile.id}`;
  return (
    <div
      data-testid={testid}
      className={`relative rounded-2xl overflow-hidden bg-brand-ink shadow-soft ${tile.featured ? "sm:col-span-2 sm:row-span-2" : ""}`}
      onMouseEnter={() => setShowing("before")}
      onMouseLeave={() => setShowing("after")}
    >
      <div className={`relative w-full ${tile.featured ? "aspect-[16/12] sm:aspect-[16/10]" : "aspect-[4/3]"} bg-brand-ink`}>
        <img
          src={tile.before}
          alt={`${tile.subject} — before`}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${showing === "before" ? "opacity-100" : "opacity-0"}`}
          loading="lazy"
          draggable={false}
        />
        <img
          src={tile.after}
          alt={`${tile.subject} — ${tile.colour}`}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${showing === "after" ? "opacity-100" : "opacity-0"}`}
          loading="lazy"
          draggable={false}
        />
      </div>
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/45 to-transparent p-4 sm:p-5 text-white">
        <div className="text-[10px] uppercase tracking-widest text-white/70 font-semibold">
          {tile.category} · {tile.subject}
        </div>
        <div className="mt-1 flex items-center gap-2 text-sm sm:text-base font-semibold">
          <span
            className="inline-block w-3.5 h-3.5 rounded-full border border-white/50 flex-shrink-0"
            style={{ backgroundColor: tile.hex }}
            aria-hidden="true"
          />
          {tile.colour}
        </div>
      </div>
      <div className="absolute top-3 left-3 bg-white/85 backdrop-blur-sm text-brand-ink text-[10px] font-bold tracking-widest uppercase px-2 py-1 rounded-full pointer-events-none">
        Hover for before
      </div>
    </div>
  );
}

/**
 * Compact horizontal swatch strip for the hero try-on demo.
 * The currently-active swatch shows an emerald ring + subtle scale-up +
 * a Check badge. On first page-load the first swatch of the wall row
 * gets a 2 s emerald "pulse" ring to nudge visitors into interacting.
 */
function SwatchRow({ label, swatches, active, onChoose, pulseHint, testidPrefix }) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-2 px-1">
        <p className="text-[10px] font-bold uppercase tracking-widest text-brand-muted">
          {label}
        </p>
        <p className="text-[10px] font-semibold text-brand-ink" data-testid={`${testidPrefix}-active-name`}>
          {active.name}
        </p>
      </div>
      <div className="grid grid-cols-2 gap-3" data-testid={`${testidPrefix}-swatches`}>
        {swatches.map((s, i) => {
          const isActive = active.id === s.id;
          const showPulse = pulseHint && i === 0; // draw the eye to the very first swatch
          return (
            <button
              key={s.id}
              onClick={() => onChoose(s)}
              data-testid={`${testidPrefix}-swatch-${s.id}`}
              aria-label={`${label}: ${s.name}`}
              aria-pressed={isActive}
              className="relative group tap-spring flex items-center gap-3 bg-white rounded-2xl border border-brand-border hover:border-brand-teal p-3 transition"
            >
              <span
                className={`relative block w-12 h-12 rounded-xl border-[3px] shadow-soft transition-all duration-200 flex-shrink-0 ${
                  isActive
                    ? "border-brand-teal ring-4 ring-brand-teal/20 ring-offset-2 ring-offset-white scale-[1.03]"
                    : "border-white group-hover:scale-[1.04]"
                }`}
                style={{ background: s.hex }}
              >
                {isActive && (
                  <span className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-brand-navy text-white shadow ring-2 ring-white flex items-center justify-center">
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  </span>
                )}
                {showPulse && !isActive && (
                  <span className="absolute inset-0 rounded-xl ring-4 ring-brand-teal animate-ping pointer-events-none" aria-hidden="true" />
                )}
              </span>
              <span className="text-left min-w-0">
                <span className="block text-sm font-semibold text-brand-ink truncate">{s.name}</span>
                <span className="block text-[10px] text-brand-muted font-mono">{s.hex.toUpperCase()}</span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function Landing() {
  const { user } = useAuth();
  const nav = useNavigate();
  const { setDraft } = useUploadDraft();
  const ctaTarget = user && user !== false ? "/visualiser/new" : "/register";
  const isSignedIn = !!user && user !== false;

  const cameraInputRef = useRef(null);
  const fileInputRef = useRef(null);

  usePageMeta({
    title: "ColourMatch — AI Paint Visualiser for Australian Homes",
    description: "ColourMatch is an AI paint visualiser (software, not a painting company). Upload your own photo and preview hundreds of interior and exterior paint colours on your home in seconds. Free first render, one-off $29.99 unlock. Built for Australian homeowners.",
    path: "/",
  });

  // Hero interactive swatch state — walls · trim · door all independently
  // chosen. Every AFTER image is a real 4-surface Gemini render.
  const [activeWall, setActiveWall] = useState(WALL_SWATCHES[0]);
  const [activeTrim, setActiveTrim] = useState(TRIM_SWATCHES[0]);
  const [activeDoor, setActiveDoor] = useState(DOOR_SWATCHES[0]);
  const [hasInteracted, setHasInteracted] = useState(false);
  const activeAfterSrc = heroSrc(activeWall.id, activeTrim.id, activeDoor.id);
  const activeCaption = `Walls in ${activeWall.name} · Trim in ${activeTrim.name} · Door in ${activeDoor.name}`;

  // Prefetch the 7 non-default combined variants so tap-to-swap is instant.
  useEffect(() => {
    WALL_SWATCHES.forEach((w) => {
      TRIM_SWATCHES.forEach((t) => {
        DOOR_SWATCHES.forEach((d) => {
          if (w.id === activeWall.id && t.id === activeTrim.id && d.id === activeDoor.id) return;
          const img = new Image();
          img.src = heroSrc(w.id, t.id, d.id);
        });
      });
    });
  }, [activeWall.id, activeTrim.id, activeDoor.id]);

  const chooseWall = (s) => { setActiveWall(s); setHasInteracted(true); };
  const chooseTrim = (s) => { setActiveTrim(s); setHasInteracted(true); };
  const chooseDoor = (s) => { setActiveDoor(s); setHasInteracted(true); };

  // Stage 2A · Upload decision gate.
  // Store the file in the single UploadDraftContext, then route by known role.
  const routeAfterUpload = useCallback(() => {
    if (!isSignedIn) {
      // Role unknown → single question.
      nav("/get-started");
      return;
    }
    if (user?.is_painter) {
      // Signed-in painter — Pro dashboard shows the "1 uploaded image waiting" pill
      // and consumes the draft via Create New or Attach to Existing.
      nav("/pro/projects");
      return;
    }
    // Signed-in homeowner → straight to the visualiser; VisualiserNew consumes the draft.
    nav("/visualiser/new");
  }, [isSignedIn, user, nav]);

  const onFilePicked = useCallback(async (file) => {
    if (!file) return;
    await setDraft(file);
    routeAfterUpload();
  }, [setDraft, routeAfterUpload]);

  const onTakePicture = useCallback(() => {
    // Mobile / tablet → native camera via capture attribute.
    // Desktop → same file picker (no webcam modal on the homepage in Stage 2A;
    // the camera-only experience remains on /visualiser/new for signed-in users).
    if (isTouchDevice()) {
      cameraInputRef.current?.click();
    } else {
      fileInputRef.current?.click();
    }
  }, []);

  const onUploadPhoto = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  return (
    <div className="bg-brand-sand" data-testid="landing-page">
      {/* ============ HERO ============ */}
      <section className="relative pt-10 pb-16 lg:pt-16 lg:pb-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <motion.div {...fadeUp} data-testid="landing-hero-text">
            <div className="inline-flex items-center gap-2 bg-white border border-brand-border rounded-full px-3 py-1.5 text-xs font-semibold text-brand-muted mb-6">
              <Sparkles size={13} className="text-brand-teal" />
              Built for Australian homeowners
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-brand-ink leading-[1.05]">
              Find the perfect colour
              <br className="hidden sm:block" />
              for your home.
            </h1>
            <p className="mt-5 text-base sm:text-lg text-brand-muted leading-relaxed max-w-xl">
              Upload a photo of your home and make confident colour decisions before you paint.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              {/* Hidden inputs — feed the single UploadDraftContext. There is only one upload. */}
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*,.heic,.heif"
                capture="environment"
                data-testid="hero-camera-input"
                className="hidden"
                onChange={(e) => onFilePicked(e.target.files?.[0])}
              />
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,.heic,.heif"
                data-testid="hero-file-input"
                className="hidden"
                onChange={(e) => onFilePicked(e.target.files?.[0])}
              />
              <button
                type="button"
                onClick={onTakePicture}
                data-testid="hero-take-picture"
                className="inline-flex items-center gap-2 bg-brand-navy hover:bg-brand-navy/90 text-white h-14 px-6 rounded-2xl text-base font-semibold tap-spring shadow-navy focus:outline-none focus:ring-2 focus:ring-brand-teal focus:ring-offset-2"
              >
                <Camera size={18} aria-hidden="true" /> Take a Picture
              </button>
              <button
                type="button"
                onClick={onUploadPhoto}
                data-testid="hero-upload-photo"
                className="inline-flex items-center gap-2 bg-white border border-brand-border hover:border-brand-teal hover:text-brand-teal text-brand-ink h-14 px-6 rounded-2xl text-base font-semibold tap-spring focus:outline-none focus:ring-2 focus:ring-brand-teal focus:ring-offset-2"
              >
                <Upload size={18} aria-hidden="true" /> Upload a Photo
              </button>
            </div>
            <p className="mt-5 text-xs text-brand-muted">
              Free first render · No credit card needed · Ready in ~15 seconds
            </p>
          </motion.div>

          <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }}>
            <BeforeAfterSlider
              before={HERO_BEFORE}
              after={activeAfterSrc}
              alt={`Queenslander home recoloured with ${activeCaption}`}
              testid="hero-before-after-slider"
            />
            <p className="mt-3 text-center text-xs text-brand-muted" data-testid="hero-slider-caption">
              Drag the divider · <span className="font-semibold text-brand-ink">{activeCaption}</span>
            </p>

            {/* Interactive swatch demo — try colours on the AFTER image */}
            <div className="mt-6 space-y-4" data-testid="hero-swatch-demo">
              <p className="text-[11px] font-bold uppercase tracking-widest text-brand-teal text-center">
                Tap a colour to try it live · 4 surfaces
              </p>

              {/* Wall row */}
              <SwatchRow
                label="Exterior walls"
                swatches={WALL_SWATCHES}
                active={activeWall}
                onChoose={chooseWall}
                pulseHint={!hasInteracted}
                testidPrefix="hero-wall"
              />

              {/* Trim row (Fascia + Gutters combined) */}
              <SwatchRow
                label="Fascia & gutters"
                swatches={TRIM_SWATCHES}
                active={activeTrim}
                onChoose={chooseTrim}
                pulseHint={false}
                testidPrefix="hero-trim"
              />

              {/* Door row */}
              <SwatchRow
                label="Front door"
                swatches={DOOR_SWATCHES}
                active={activeDoor}
                onChoose={chooseDoor}
                pulseHint={false}
                testidPrefix="hero-door"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* ============ TRUST BAND ============ */}
      <section className="border-t border-brand-border bg-white" data-testid="landing-trust-band">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10 lg:py-14">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-8">
            {TRUST.map((t) => {
              const Icon = t.icon;
              return (
                <div key={t.label} className="flex items-start gap-3" data-testid={`trust-${t.label.toLowerCase().replace(/\s+/g,'-')}`}>
                  <span className="w-11 h-11 rounded-2xl bg-brand-teal/10 text-brand-teal flex items-center justify-center flex-shrink-0">
                    <Icon size={19} />
                  </span>
                  <div>
                    <div className="font-semibold text-brand-ink text-sm sm:text-base">{t.label}</div>
                    <div className="mt-0.5 text-xs sm:text-sm text-brand-muted leading-snug">{t.sub}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ REAL COLOURMATCH RENDER GALLERY ============ */}
      <section className="py-16 lg:py-24" data-testid="landing-gallery">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <motion.div {...fadeUp} className="max-w-2xl mb-10 lg:mb-14">
            <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-brand-teal mb-3">
              Real ColourMatch renders
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-brand-ink leading-[1.1]">
              Works across every room. And every façade.
            </h2>
            <p className="mt-4 text-base text-brand-muted leading-relaxed">
              Hover any tile to see the &ldquo;before&rdquo;. Every image is a genuine ColourMatch render — the exact
              output you&rsquo;d get on your own photo.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5 auto-rows-auto">
            {GALLERY.map((tile) => <GalleryTile key={tile.id} tile={tile} />)}
          </div>

          <motion.div {...fadeUp} className="mt-10 lg:mt-14 text-center">
            <Link
              to={ctaTarget}
              data-testid="gallery-cta"
              className="inline-flex items-center gap-2 bg-brand-ink hover:bg-brand-ink/90 text-white h-14 px-7 rounded-2xl text-base font-semibold tap-spring"
            >
              Try it on your home <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ============ HOW IT WORKS ============ */}
      <section className="py-16 lg:py-24 bg-white border-t border-brand-border" data-testid="landing-how-it-works">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <motion.div {...fadeUp} className="max-w-2xl mb-10 lg:mb-14">
            <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-brand-teal mb-3">
              How it works
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-brand-ink leading-[1.1]">
              Three steps. About a minute.
            </h2>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {[
              { n: "01", h: "Upload a photo", p: "Any front-facing photo of your home exterior or a room. Straight from your phone works." },
              { n: "02", h: "Pick a colour",  p: "Choose from 1,000+ designer-curated colours or match a specific hex. Surface library covers walls, doors, roofs and more." },
              { n: "03", h: "See the result", p: "Ready in ~15 seconds. Free first render. Unlock the full project for a one-off $29.99." },
            ].map((s, i) => (
              <motion.div key={s.n} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.08 }}
                className="bg-brand-sand border border-brand-border rounded-3xl p-6 lg:p-7"
                data-testid={`how-step-${s.n}`}
              >
                <div className="text-3xl font-extrabold text-brand-teal tracking-tight mb-2">{s.n}</div>
                <h3 className="text-lg font-bold text-brand-ink mb-1.5">{s.h}</h3>
                <p className="text-sm text-brand-muted leading-relaxed">{s.p}</p>
              </motion.div>
            ))}
          </div>
          <div className="mt-10 lg:mt-14">
            <Link
              to={ctaTarget}
              data-testid="how-cta"
              className="inline-flex items-center gap-2 bg-brand-navy hover:bg-brand-navy/90 text-white h-14 px-6 rounded-2xl text-base font-semibold tap-spring shadow-navy"
            >
              Try on your photo <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Homeowner pricing (Stage 1 · 2026-02) — inline card, single source of truth
          for homeowner tier. Full breakdown available at /pricing. */}
      <section className="py-16 lg:py-24 bg-brand-sand border-t border-brand-border" data-testid="landing-homeowner-pricing">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <motion.div {...fadeUp} className="max-w-2xl mb-8 lg:mb-10">
            <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-brand-teal mb-3">
              Homeowner pricing
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-brand-ink leading-[1.1]">
              Try free. Unlock your project for $29.99.
            </h2>
            <p className="mt-3 text-brand-muted leading-relaxed">
              No subscription. No hidden fees. Your unlocked project is yours to revisit any time.
            </p>
          </motion.div>
          <div className="grid md:grid-cols-2 gap-5">
            <div
              data-testid="homeowner-tier-free"
              className="bg-white border border-brand-border rounded-3xl p-7 flex flex-col"
            >
              <div className="flex items-baseline justify-between">
                <h3 className="text-lg font-bold text-brand-ink">Free</h3>
                <div className="text-3xl font-extrabold text-brand-ink">$0</div>
              </div>
              <ul className="mt-4 space-y-2 text-sm text-brand-ink flex-1">
                <li className="flex items-start gap-2"><span className="mt-1 w-1.5 h-1.5 rounded-full bg-brand-teal flex-shrink-0" /><span>First render on the house</span></li>
                <li className="flex items-start gap-2"><span className="mt-1 w-1.5 h-1.5 rounded-full bg-brand-teal flex-shrink-0" /><span>1,000+ curated designer colours</span></li>
                <li className="flex items-start gap-2"><span className="mt-1 w-1.5 h-1.5 rounded-full bg-brand-teal flex-shrink-0" /><span>Preview interior and exterior</span></li>
              </ul>
              <Link
                to={ctaTarget}
                data-testid="homeowner-tier-free-cta"
                className="mt-6 inline-flex items-center justify-center gap-2 border border-brand-navy text-brand-navy h-11 px-5 rounded-2xl text-sm font-semibold hover:bg-brand-cream transition-all"
              >
                Start free
              </Link>
            </div>

            <div
              data-testid="homeowner-tier-project"
              className="bg-white border border-brand-border rounded-3xl p-7 flex flex-col"
            >
              <div className="flex items-baseline justify-between">
                <h3 className="text-lg font-bold text-brand-ink">Project unlock</h3>
                <div className="text-right">
                  <div className="text-3xl font-extrabold text-brand-ink leading-none">
                    $29.99<span className="text-sm font-medium text-brand-muted"> one-off</span>
                  </div>
                  <div className="text-xs text-brand-muted mt-1">per project · no subscription</div>
                </div>
              </div>
              <ul className="mt-4 space-y-2 text-sm text-brand-ink flex-1">
                <li className="flex items-start gap-2"><span className="mt-1 w-1.5 h-1.5 rounded-full bg-brand-teal flex-shrink-0" /><span>Unlimited renders on the project</span></li>
                <li className="flex items-start gap-2"><span className="mt-1 w-1.5 h-1.5 rounded-full bg-brand-teal flex-shrink-0" /><span>HD downloads + paint specification PDF</span></li>
                <li className="flex items-start gap-2"><span className="mt-1 w-1.5 h-1.5 rounded-full bg-brand-teal flex-shrink-0" /><span>Save concepts and compare side-by-side</span></li>
              </ul>
              <Link
                to={ctaTarget}
                data-testid="homeowner-tier-project-cta"
                className="mt-6 inline-flex items-center justify-center gap-2 bg-brand-navy text-white h-11 px-5 rounded-2xl text-sm font-semibold hover:bg-brand-navy/90 transition-all"
              >
                Start your project <ArrowRight size={14} />
              </Link>
            </div>
          </div>
          <p className="mt-6 text-xs text-brand-muted">
            Prices in AUD. Payments processed by Stripe. Full breakdown at <Link to="/pricing" className="text-brand-teal hover:underline">pricing overview</Link>.
          </p>
        </div>
      </section>
    </div>
  );
}
